import * as React from 'react'
import styled from 'styled-components'

import * as ds from '@bob/design-system'
import { styles as S } from '@bob/design-system'

import * as hooks from '@bob/hooks'

export type FiltersItems = {
  filterSlug: string
  filterTitle: string
  filterValues: FilterValue[]
}[]

export type FilterValue = {
  id: string
  isSelected: boolean
  name: string
}

type FiltersProps<ITEM> = {
  engine: hooks.useSearchEngine.SearchEngine<ITEM>
  filters: FiltersItems
}

type Query<ITEM> = hooks.useSearchEngine.SearchEngine<ITEM>['query']

export function Filters<ITEM>({ engine, filters }: FiltersProps<ITEM>): React.ReactElement | null {
  const [focused, setFocused] = React.useState<string | undefined>(filters[0]?.filterSlug)

  const numberSelectedFilter = getNumberSelectedFilters(engine.query)

  // Séparation en deux pane différent
  // parce que le mobile pane utilise overlay
  // et cela pose des soucis sur desktop
  return (
    <FilterContainer>
      <MobilePane
        engine={engine}
        filters={filters}
        focused={focused}
        numberSelectedFilter={numberSelectedFilter}
        setFocused={setFocused}
      />
      <DesktopPane
        engine={engine}
        filters={filters}
        focused={focused}
        numberSelectedFilter={numberSelectedFilter}
        setFocused={setFocused}
      />
    </FilterContainer>
  )
}

type Focused = string | undefined
type SetFocused = React.Dispatch<React.SetStateAction<string | undefined>>
type PaneProps<ITEM> = Omit<FiltersProps<ITEM>, 'ready'> & {
  focused: Focused
  numberSelectedFilter: number
  setFocused: SetFocused
}
type DesktopPaneProps<ITEM> = Omit<PaneProps<ITEM>, 'numberArticles'>

function MobilePane<ITEM>({
  engine,
  filters,
  focused,
  numberSelectedFilter,
  setFocused,
}: PaneProps<ITEM>): React.ReactElement {
  const [openPane, setOpenPane] = React.useState(false)

  return (
    <MobilePanContainer>
      <ds.Button
        onClick={() => {
          setOpenPane(true)
          setFocused(undefined)
        }}
        iconLeft={<ds.icons.Filter />}
        outlined={true}
      >
        {engine.ready ? `Filtrer${numberSelectedFilter > 0 ? ` (${numberSelectedFilter})` : ''}` : 'Chargement'}
      </ds.Button>
      {openPane && (
        <Pane>
          <FilterHeading>
            <S.heading.TitleCard>Filtres</S.heading.TitleCard>
            {Object.keys(engine.query.value.filters).length !== 0 && (
              <ds.Button as={S.link.Base} onClick={() => engine.query.reset()}>
                Réinitialiser les filtres
              </ds.Button>
            )}
            <ButtonClose
              onClick={() => {
                setOpenPane(false)
              }}
            >
              <ds.icons.Cross size={20} />
            </ButtonClose>
          </FilterHeading>
          <FilterList filters={filters} focused={focused} engine={engine} setFocused={setFocused} />
          <BackButton
            onClick={() => {
              setFocused(undefined)
              setOpenPane(false)
            }}
          >
            Voir la sélection ({engine.result.total})
          </BackButton>
        </Pane>
      )}
    </MobilePanContainer>
  )
}

function DesktopPane<ITEM>({
  engine,
  filters,
  focused,
  numberSelectedFilter,
  setFocused,
}: DesktopPaneProps<ITEM>): React.ReactElement {
  return (
    <DesktopPaneContainer>
      <ds.Button
        onClick={() => {
          setFocused('')
        }}
        iconLeft={<ds.icons.Filter />}
      >
        Filtrer {numberSelectedFilter > 0 && `(${numberSelectedFilter})`}
      </ds.Button>
      <>
        <FilterHeading>
          {engine.ready ? (
            filters.length > 0 && <S.heading.TitleCard>Filtres</S.heading.TitleCard>
          ) : (
            <S.heading.TitleCard>Filtres</S.heading.TitleCard>
          )}

          {Object.keys(engine.query.value.filters).length !== 0 && (
            <ds.Button as={S.link.Base} onClick={() => engine.query.reset()}>
              Réinitialiser les filtres
            </ds.Button>
          )}
        </FilterHeading>
        <FilterList filters={filters} focused={focused} engine={engine} setFocused={setFocused} />
      </>
    </DesktopPaneContainer>
  )
}

type FilterListProps<ITEM> = Omit<FiltersProps<ITEM>, 'ready' | 'numberArticles'> & {
  focused: Focused
  setFocused: SetFocused
}

function FilterList<ITEM>({ engine: { query, ready }, filters, focused, setFocused }: FilterListProps<ITEM>) {
  return (
    <FilterWrapper>
      {!ready && Array.from({ length: 5 }, (_, i) => <FilterSkeleton key={i} />)}
      {ready &&
        filters.map(({ filterSlug, filterTitle, filterValues }, index) => {
          return (
            <ds.Filter
              onToggle={(open) => {
                if (!open) {
                  setFocused('')
                } else {
                  setFocused(filterSlug)
                }
              }}
              open={focused === filterSlug}
              key={index}
              title={filterTitle}
              filters={filterValues}
              onSelectionChange={(name, add) => {
                if (add) {
                  query.add(filterSlug, name)
                } else {
                  query.remove(filterSlug, name)
                }
              }}
            />
          )
        })}
    </FilterWrapper>
  )
}

function getNumberSelectedFilters<ITEM>(query: Query<ITEM>) {
  const selectedFilters = query.value.filters
  return Object.values(selectedFilters).reduce((totalLength, filters) => {
    return totalLength + filters.length
  }, 0)
}

const MobilePanContainer = styled.div`
  display: none;

  @media not screen and (${ds.breakpoint.TABLET}) {
    display: block;
  }
`

const DesktopPaneContainer = styled.div`
  display: block;

  @media not screen and (${ds.breakpoint.TABLET}) {
    display: none;
  }
`

const BackButton = styled(ds.Button)`
  bottom: 0;
  left: 0;
  margin: 25px;
  position: fixed;
  z-index: 2;

  @media screen and (${ds.breakpoint.TABLET}) {
    display: none;
  }
`

const ButtonClose = styled(S.button.UnstyledButton)`
  position: absolute;
  right: 0;
  top: 2px;
`

const FilterHeading = styled.div`
  position: relative;

  ${S.heading.TitleCard} {
    margin-bottom: 16px;
  }

  ${ds.Button} {
    text-align: right;
  }

  @media screen and (${ds.breakpoint.TABLET}) {
    align-items: center;
    display: flex;
    width: 100%;

    ${S.heading.TitleCard} {
      margin-bottom: 0;
      flex: 1;
    }
  }
`

const FilterContainer = styled.div`
  flex: 1;
  margin-right: 3px;
  width: 100%;

  ${S.link.Base} {
    cursor: pointer;
  }

  ${ds.Button} {
    width: 100%;
  }

  ${BackButton} {
    width: calc(100% - 50px);
  }

  @media screen and (${ds.breakpoint.TABLET}) {
    margin-top: 25px;

    ${ds.Button} {
      display: none;
    }

    ${ButtonClose} {
      display: none;
    }

    ${S.link.Base} {
      display: block;
    }
  }
`

const FilterWrapper = styled.div`
  margin-top: 23px;
  overflow: scroll;
  height: calc(100% - 170px);

  ${S.filter.AccordionWrapper} {
    background-color: ${ds.color.SAND_2};
    border-radius: 10px;
    margin-bottom: 10px;
    padding: 15px 0;
  }

  ${S.accordion.Title} {
    padding: 0 18px;
  }

  @media screen and (${ds.breakpoint.TABLET}) {
    margin-top: 10px;

    ${S.accordion.Content} {
      margin-top: 5px;
    }

    ${S.accordion.Title} {
      padding: 0;
    }

    ${S.filter.AccordionWrapper} {
      padding: 15px 18px;
    }
  }
`

const Pane = styled(ds.Overlay)`
  @media not screen and (${ds.breakpoint.TABLET}) {
    display: block;
    background-color: ${ds.color.SAND_1};
    height: 100%;
    left: 0;
    padding: 25px;
    position: fixed;
    top: ${ds.sizes.HEADER_MOBILE_INTERNAL_HEIGHT};
    width: 100vw;
    z-index: 2;
  }
`

/* skeleton */
function FilterSkeletonComponent() {
  return (
    <ds.Filter
      title={<ds.styles.skeleton.Text size="small" width={80} />}
      filters={[]}
      open={false}
      onSelectionChange={() => null}
      onToggle={() => null}
    />
  )
}

export const FilterSkeleton = styled(FilterSkeletonComponent)``
